import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Panel } from "@/components/AppShell";

export const missingText = (missing) => {
  const s = missing.filter((m) => m.starter), b = missing.filter((m) => !m.starter);
  const parts = [s.length && `${s.length} starter${s.length > 1 ? "s" : ""}`, b.length && `${b.length} bench`].filter(Boolean);
  return `${parts.join(", ")} · ${missing.reduce((n, m) => n + m.minutes, 0).toFixed(1)} min without season data`;
};

// Admin minutes for players the new model can't rate yet; they then use the team's starter/bench season rate.
export function NewModelMinutes({ gameId, teamId }) {
  const [d, setD] = useState(null);
  const [vals, setVals] = useState({});
  const load = useCallback(() => api.get("/season-model/team", { params: { game_id: gameId, team_id: teamId } })
    .then((r) => { setD(r.data); setVals(Object.fromEntries(Object.entries(r.data.manual_minutes || {}).map(([k, v]) => [k, String(v)]))); })
    .catch((e) => setD({ error: errorText(e) })), [gameId, teamId]);
  useEffect(() => { setD(null); load(); }, [load]);
  if (!d) return null;
  if (d.error) return <Panel className="p-4" data-testid="nm-minutes"><p className="text-rose-400 text-sm" data-testid="nm-error">{d.error}</p></Panel>;
  const clearAll = async () => {
    try { await api.put("/season-model/manual-minutes", { game_id: gameId, team_id: teamId, minutes: Object.fromEntries(Object.keys(d.manual_minutes || {}).map((k) => [k, null])) }); toast.success("Minutes cleared"); load(); } catch (e) { toast.error(errorText(e)); }
  };
  const rows = (d.players || []).filter((p) => !p.out && (!p.has_rate || p.manual));
  const save = async () => {
    const minutes = Object.fromEntries(rows.map((p) => [p.player_id, vals[p.player_id] === "" || vals[p.player_id] == null ? null : Number(vals[p.player_id])]));
    try { await api.put("/season-model/manual-minutes", { game_id: gameId, team_id: teamId, minutes }); toast.success("New model minutes saved"); load(); } catch (e) { toast.error(errorText(e)); }
  };
  const done = d.status === "complete" && Math.abs((d.minutes_total || 0) - 240) < 0.01;
  return (
    <Panel className="p-4" data-testid="nm-minutes">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h3 className="flex items-center gap-2 font-bold tracking-wider text-white"><FlaskConical className="h-4 w-4 text-amber-300" />NEW MODEL · MISSING MINUTES</h3>
        <span data-testid="nm-status" className={`text-xs font-bold px-2 py-1 rounded ${done ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{done ? "240/240 COMPLETE" : "PARTIAL"}</span>
        {d.missing?.length > 0 && <span className="text-sm text-amber-300" data-testid="nm-missing">{missingText(d.missing)}</span>}
      </div>
      {!done && d.reason && !d.missing?.length && <p className="text-sm text-slate-400 mb-2" data-testid="nm-reason">{d.reason}</p>}
      {Object.keys(d.manual_minutes || {}).length > 0 && <button type="button" onClick={clearAll} className="mb-3 rounded-md border border-slate-600 px-3 py-1 text-xs text-slate-300" data-testid="nm-clear">Clear all admin minutes</button>}
      {rows.length === 0 ? <p className="text-sm text-slate-500">Every available player has season data.</p> : (
        <>
          <p className="text-xs text-slate-500 mb-3">Minutes you enter are fixed; the player uses the team's season {""}starter/bench per-minute rate. Leave empty to clear.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {rows.map((p) => (
              <label key={p.player_id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm" data-testid={`nm-row-${p.player_id}`}>
                <span className="min-w-0"><b className="text-white">{p.name}</b><small className="block text-slate-500">{p.starter ? "Starter" : "Bench"} · model {p.minutes.toFixed(1)} min{p.manual ? " · admin" : ""}</small></span>
                <input type="number" min="0" max="48" step="0.5" value={vals[p.player_id] ?? ""} onChange={(e) => setVals({ ...vals, [p.player_id]: e.target.value })}
                  className="w-20 rounded-md border border-slate-600 bg-slate-950 px-2 py-1 text-white" data-testid={`nm-input-${p.player_id}`} />
              </label>
            ))}
          </div>
          <button type="button" onClick={save} disabled={d.game_status !== "pre"} className="mt-3 rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40" data-testid="nm-save">Save minutes</button>
        </>
      )}
    </Panel>
  );
}
